// src/pages/WebLoginPage.tsx
// Simplified login page — uses unified /auth endpoint
// ~250 lines (was ~605 lines)

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore, useStudioStore } from '@/store'
import { supabase } from '@/lib/supabase'
import { callAuth } from '@/lib/authClient'
import { formatAuthErrorMessage } from '@/lib/authErrorMessages'
import { mapRawStudiosToStore } from '@/lib/studioMapper'
import {
  buildInviteAwareLoginPath,
  clearPendingInvite,
  readPendingInvite,
  savePendingInvite
} from '@/lib/invitePersistence'
import { useTelegram } from '@/hooks/useTelegram'
import { Card } from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { LogIn, XCircle, Loader2, Mail, CheckCircle2, ShieldCheck } from 'lucide-react'

export function WebLoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [emailSending, setEmailSending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const { login, isAuthenticated } = useAuthStore()
  const { setStudios } = useStudioStore()
  const { isInTelegram } = useTelegram()
  const normalizedEmail = email.trim().toLowerCase()
  const inviteTokenFromUrl = searchParams.get('invite_token')
  const isTelegramLinkMode = searchParams.get('link_telegram') === '1'
  const isAuthCallbackInProgress =
    searchParams.has('auth') ||
    searchParams.has('email_token') ||
    searchParams.has('code') ||
    searchParams.has('hash') ||
    searchParams.has('id')
  // Токен без явного auto-accept не является согласием принять приглашение. Обычный вход
  // не должен подхватывать старую ссылку из localStorage и применять её к другому аккаунту.
  // В нормальном invite-потоке токен присутствует в URL и в ссылке из email; storage нужен
  // только как fallback callback-а после уже нажатой кнопки «Принять».
  const persistedInviteToken = readPendingInvite({ requireAutoAccept: true })?.token ?? null
  const effectiveInviteToken = inviteTokenFromUrl ||
    ((isAuthCallbackInProgress || isTelegramLinkMode) ? persistedInviteToken || undefined : undefined)
  const isInviteDrivenLogin = Boolean(effectiveInviteToken) && !isTelegramLinkMode
  const isEmailFormatValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
  const isLocalDevHost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  const isDevTelegramStubEnabled = import.meta.env.DEV && isLocalDevHost
  const callbackHandledRef = useRef(false)
  const telegramWidgetContainerRef = useRef<HTMLDivElement | null>(null)
  const [telegramWidgetReady, setTelegramWidgetReady] = useState(false)
  const [telegramWidgetSlow, setTelegramWidgetSlow] = useState(false)
  const [telegramWidgetFailed, setTelegramWidgetFailed] = useState(false)

  function navigateAfterAuth(inviteToken?: string | null) {
    // Always clean returnTo to avoid stale redirects
    const returnTo = sessionStorage.getItem('returnTo') || '/'
    sessionStorage.removeItem('returnTo')

    if (inviteToken) {
      savePendingInvite(inviteToken, { autoAccept: true })
      navigate(`/invite/${inviteToken}`, { replace: true })
      return
    }

    // Clean up stale invite token if the current login was not invite-driven
    clearPendingInvite()
    navigate(returnTo, { replace: true })
  }

  function navigateToCleanLoginPreservingInvite() {
    navigate(buildInviteAwareLoginPath(effectiveInviteToken), { replace: true })
  }

  function handleAuthSuccess(result: { token: string; user: any; studios: any[] }, inviteToken?: string | null) {
    localStorage.setItem('auth_token', result.token)
    setStudios(mapRawStudiosToStore(result.studios || []))
    login({
      ...result.user,
      created_at: result.user.created_at || new Date().toISOString(),
      updated_at: result.user.updated_at || new Date().toISOString(),
      studios: result.studios || []
    })
    navigateAfterAuth(inviteToken)
  }

  // Redirect if already authenticated (skip during active callback processing)
  useEffect(() => {
    if (isAuthenticated && !isAuthCallbackInProgress && !isTelegramLinkMode) navigateAfterAuth(effectiveInviteToken)
  }, [isAuthenticated, isTelegramLinkMode, isAuthCallbackInProgress, effectiveInviteToken])

  // In Telegram — redirect to home (AuthProvider handles auth)
  useEffect(() => {
    if (isInTelegram) navigate('/', { replace: true })
  }, [isInTelegram, navigate])

  // ─── Telegram Widget callback (URL params contain id, hash, etc.) ───────────
  useEffect(() => {
    const telegramId = searchParams.get('id')
    const hash = searchParams.get('hash')
    if (!telegramId || !hash || callbackHandledRef.current) return
    callbackHandledRef.current = true
    handleTelegramWidgetCallback()
  }, [searchParams])

  // ─── New email verify callback (URL contains email_token) ───────────────────
  useEffect(() => {
    const emailToken = searchParams.get('email_token')
    const emailParam = searchParams.get('email')
    const ts = searchParams.get('ts')
    if (!emailToken || !emailParam || !ts || callbackHandledRef.current) return
    callbackHandledRef.current = true
    handleEmailVerifyCallback(emailParam, emailToken, Number(ts))
  }, [searchParams])

  // ─── Legacy email login callback (Supabase Auth OTP — transition period) ────
  useEffect(() => {
    const authIntent = searchParams.get('auth')
    if (authIntent !== 'email-login' && authIntent !== 'email' && authIntent !== 'email-verify') return
    if (callbackHandledRef.current) return

    setLoading(true)
    setError(null)

    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let authSubscription: { unsubscribe: () => void } | null = null

    // Try to get Supabase session and exchange via legacy endpoint
    const handleLegacy = async () => {
      try {
        // Check existing session
        const { data: { session } } = await supabase.auth.getSession()
        let accessToken = session?.access_token

        // PKCE fallback
        if (!accessToken) {
          const code = searchParams.get('code')
          if (code) {
            const { error } = await supabase.auth.exchangeCodeForSession(code)
            if (!error) {
              const { data: { session: codeSession } } = await supabase.auth.getSession()
              accessToken = codeSession?.access_token
            }
          }
        }

        // Hash fragment fallback
        if (!accessToken && typeof window !== 'undefined') {
          const rawHash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
          if (rawHash) accessToken = new URLSearchParams(rawHash).get('access_token') ?? undefined
        }

        if (cancelled) return

        if (!accessToken) {
          // Wait for onAuthStateChange
          timeoutId = setTimeout(() => {
            if (cancelled || callbackHandledRef.current) return
            setLoading(false)
            setError('Не удалось завершить вход по email. Попробуйте снова.')
          }, 12000)

          const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, sess) => {
            if (cancelled || callbackHandledRef.current) return
            if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && sess?.access_token) {
              callbackHandledRef.current = true
              if (timeoutId) clearTimeout(timeoutId)
              subscription.unsubscribe()
              await exchangeLegacyEmail(sess.access_token, authIntent === 'email-verify' ? 'email-verify' : 'email-login')
            }
          })
          authSubscription = subscription
          return
        }

        callbackHandledRef.current = true
        await exchangeLegacyEmail(accessToken, authIntent === 'email-verify' ? 'email-verify' : 'email-login')
      } catch (err) {
        if (cancelled) return
        setLoading(false)
        setError(err instanceof Error ? err.message : 'Ошибка входа по email')
      }
    }

    handleLegacy()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      if (authSubscription) authSubscription.unsubscribe()
    }
  }, [searchParams])

  async function handleTelegramWidgetCallback() {
    setLoading(true)
    setError(null)

    try {
      const widgetData: Record<string, string> = {}
      for (const [key, value] of searchParams.entries()) {
        widgetData[key] = value
      }

      const result = await callAuth('widget', {
        widgetData,
        invite_token: effectiveInviteToken,
        link_current_user: isTelegramLinkMode
      }, {
        useUserJwt: isTelegramLinkMode
      })
      handleAuthSuccess(result as any, effectiveInviteToken)
    } catch (err) {
      setError(formatAuthErrorMessage(err, 'telegram'))
      navigateToCleanLoginPreservingInvite()
    } finally {
      setLoading(false)
    }
  }

  async function handleEmailVerifyCallback(emailParam: string, emailToken: string, ts: number) {
    setLoading(true)
    setError(null)

    try {
      const result = await callAuth('email-verify', { email: emailParam, emailToken, ts, invite_token: effectiveInviteToken })
      handleAuthSuccess(result as any, effectiveInviteToken)
    } catch (err) {
      setError(formatAuthErrorMessage(err, 'email'))
      navigateToCleanLoginPreservingInvite()
    } finally {
      setLoading(false)
    }
  }

  // Legacy: exchange Supabase access token for app token (transition period)
  async function exchangeLegacyEmail(accessToken: string, intent: 'email-login' | 'email-verify' = 'email-login') {
    setLoading(true)
    setError(null)

    try {
      // Use direct fetch to email-auth-callback — it needs the Supabase access_token as Bearer
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-auth-callback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ intent })
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload?.error || 'Ошибка email авторизации')

      handleAuthSuccess(
        { token: payload.token, user: payload.user, studios: payload.studios || [] },
        effectiveInviteToken
      )
    } catch (err) {
      setError(formatAuthErrorMessage(err, 'email'))
      await supabase.auth.signOut()
      navigateToCleanLoginPreservingInvite()
    } finally {
      setLoading(false)
    }
  }

  async function sendEmailLink() {
    setEmailSending(true)
    setError(null)
    setEmailSent(false)

    try {
      await callAuth('email-request', {
        email: normalizedEmail,
        invite_token: effectiveInviteToken
      })
      setEmailSent(true)
    } catch (err) {
      setError(formatAuthErrorMessage(err, 'email'))
    } finally {
      setEmailSending(false)
    }
  }

  // Load Telegram Widget script
  useEffect(() => {
    if (isDevTelegramStubEnabled || error || !telegramWidgetContainerRef.current) return

    const container = telegramWidgetContainerRef.current
    container.innerHTML = ''
    setTelegramWidgetReady(false)
    setTelegramWidgetSlow(false)
    setTelegramWidgetFailed(false)

    const script = document.createElement('script')
    const authUrl = new URL(`${window.location.origin}/login`)
    if (effectiveInviteToken) {
      authUrl.searchParams.set('invite_token', effectiveInviteToken)
    }
    if (isTelegramLinkMode) {
      authUrl.searchParams.set('link_telegram', '1')
    }

    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.setAttribute('data-telegram-login', import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'your_bot_name')
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-auth-url', authUrl.toString())
    script.setAttribute('data-request-access', 'write')
    script.async = true
    script.onload = () => {
      setTelegramWidgetReady(true)
      setTelegramWidgetSlow(false)
      setTelegramWidgetFailed(false)
    }
    script.onerror = () => {
      setTelegramWidgetReady(false)
      setTelegramWidgetFailed(true)
    }

    const slowTimer = window.setTimeout(() => {
      setTelegramWidgetSlow(true)
    }, 2500)

    const failTimer = window.setTimeout(() => {
      setTelegramWidgetFailed(true)
    }, 12000)

    container.appendChild(script)

    return () => {
      window.clearTimeout(slowTimer)
      window.clearTimeout(failTimer)
      if (container.contains(script)) container.removeChild(script)
    }
  }, [isDevTelegramStubEnabled, error, effectiveInviteToken, isTelegramLinkMode])

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium mb-2">Вход в систему</h3>
          <p className="text-muted-foreground">Пожалуйста, подождите...</p>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <XCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Ошибка входа</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => { setError(null); callbackHandledRef.current = false }} className="w-full">
            Вернуться ко входу
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/40 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <LogIn className="w-11 h-11 text-primary mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Вход в Ассистент LEVITA</h2>
          <p className="text-sm text-muted-foreground">
            {isTelegramLinkMode
              ? 'Подтвердите Telegram, чтобы привязать его к текущему аккаунту.'
              : 'Выберите удобный способ входа. После авторизации вы попадете в ваш рабочий кабинет.'}
          </p>
        </div>

        {isInviteDrivenLogin && (
          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 mb-4">
            <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-foreground">
              Вы входите по приглашению. Укажите тот email, на который его выдали, —
              приглашение применится сразу после входа.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Вход по Email</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {isTelegramLinkMode
                ? 'Этот режим нужен именно для привязки Telegram. Email-вход здесь можно пропустить.'
                : 'Получите ссылку для входа на почту. Подходит для браузера без Telegram.'}
            </p>
            <div className="space-y-2">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailSent(false) }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !emailSending && isEmailFormatValid) void sendEmailLink() }}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                disabled={emailSending}
              />
              {email.length > 0 && !isEmailFormatValid && (
                <p className="text-xs text-red-600">Проверьте формат email</p>
              )}
              {emailSent && (
                <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <p className="text-xs text-emerald-800">
                    Ссылка отправлена. Откройте письмо и перейдите по ссылке для входа.
                  </p>
                </div>
              )}
              <Button
                onClick={() => void sendEmailLink()}
                disabled={emailSending || !isEmailFormatValid}
                className="w-full inline-flex items-center justify-center"
              >
                {emailSending ? (
                  <><Loader2 className="h-4 w-4" /> Отправляем ссылку...</>
                ) : (
                  <>Получить ссылку для входа</>
                )}
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Вход по Telegram ID</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {isTelegramLinkMode
                ? 'Войдите через Telegram, и этот аккаунт будет привязан к вашему текущему профилю.'
                : 'Подтвердите, что это вы через аккаунт Telegram и продолжите работу в браузере.'}
            </p>
            <div>
              {isDevTelegramStubEnabled ? (
                <button
                  type="button"
                  onClick={() => navigate('/', { replace: true })}
                  className="w-full min-h-[44px] rounded-[22px] bg-[#54A9EB] px-4 text-[15px] font-semibold text-white hover:bg-[#4a9bdb]"
                >
                  Log in with Telegram
                </button>
              ) : (
                <div className="space-y-3">
                  <div ref={telegramWidgetContainerRef} className="flex justify-center min-h-[44px]" />

                  {!telegramWidgetReady && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Loader2 className={`h-4 w-4 ${telegramWidgetFailed ? '' : 'animate-spin'}`} />
                        <span>
                          {telegramWidgetFailed
                            ? 'Виджет Telegram пока недоступен. Можно войти по email слева.'
                            : telegramWidgetSlow
                              ? 'Telegram загружается дольше обычного. Вход по email уже доступен.'
                              : 'Загружаем кнопку входа через Telegram...'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="text-sm font-semibold text-foreground mb-1">Уйти из браузера в приложение Telegram</p>
          <p className="text-xs text-muted-foreground mb-3">
            Приложение откроется внутри Telegram Mini App.
          </p>
          <a
            href={`https://t.me/${import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'your_bot_name'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full md:w-auto px-4 py-2.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Открыть в Telegram
          </a>
        </div>
      </Card>
    </div>
  )
}
